<!--
  IGNTR "choose tenant" page.
  All wiring (tenantsOptions, setActiveTenant, skip, chooseRedirectAction,
  showGdprConfirmation, Office 365 settings) is identical to the previous
  implementation. The layout is a fixed-height column:

    header  ────────── shrink-0
    main    flex-1 / overflow-hidden
      heading + search   shrink-0
      tenant list        flex-1 / overflow-y-auto   ← only this scrolls
    action bar           shrink-0
    footer               shrink-0

  The badges, member counts and "last accessed" strings are still
  design-only — wire them when backend fields land.
-->
<div
  *ngIf="showPageContent"
  class="igntr-theme igntr-canvas flex h-screen w-full flex-col bg-canvas font-sans text-slate-900"
>
  <!-- Top utility bar -->
  <header class="flex shrink-0 items-center justify-between px-4 pt-3 sm:items-start sm:px-8 sm:pt-7 lg:px-16">
    <a
      routerLink="/signin"
      class="inline-flex items-center gap-2 text-[13px] text-slate-500 no-underline hover:text-slate-800"
      [attr.aria-label]="'backToLogin' | transloco"
    >
      <igntr-icon name="chevronL" [size]="16"></igntr-icon>
      <span class="hidden sm:inline">{{ "backToLogin" | transloco }}</span>
    </a>
    <div class="flex items-center gap-3 text-right text-[12.5px]">
      <div class="hidden sm:block">
        <div class="text-slate-400">{{ "loggedInAs" | transloco }}</div>
        <div class="font-mono text-slate-700">{{ currentUserEmail }}</div>
      </div>
      <div
        class="flex h-9 w-9 items-center justify-center rounded-full text-[11px] font-semibold text-white"
        style="background: linear-gradient(135deg, oklch(0.72 0.12 30), oklch(0.55 0.15 20));"
      >
        {{ currentUserInitials }}
      </div>
      <button
        type="button"
        (click)="skip()"
        class="rounded-full p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
        [attr.aria-label]="'skipForNow' | transloco"
      >
        <igntr-icon name="logout" [size]="16"></igntr-icon>
      </button>
    </div>
  </header>

  <!-- Center column — flex-1 so it fills the gap, overflow-hidden so the
       inner list owns the only scrollbar. -->
  <main class="mx-auto flex w-full max-w-[720px] flex-1 flex-col items-center overflow-hidden px-6 pt-4">
    <!-- Brand mark — hidden on mobile to free up vertical space -->
    <div
      class="mb-2 hidden sm:mb-5 sm:flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white shadow-[0_4px_24px_rgba(15,23,42,0.06)] ring-1 ring-slate-100"
    >
      <igntr-flame [size]="24"></igntr-flame>
    </div>

    <h1 class="mb-2 shrink-0 text-center font-serif text-[26px] sm:text-[40px] font-semibold leading-tight tracking-[-0.01em] text-slate-900">
      {{ "choose" | transloco }} {{ "yourAT" | transloco }}
    </h1>
    <p class="mb-3 sm:mb-6 hidden sm:block max-w-[560px] shrink-0 text-center text-[14px] leading-relaxed text-slate-500">
      {{ "chooseTenantSubtitle" | transloco }}
    </p>

    <!-- Search row -->
    <div
      class="mb-3 sm:mb-5 flex w-full shrink-0 items-center gap-2 rounded-2xl bg-white px-4 py-2.5 sm:py-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
      style="border: 1px solid #e2e8f0;"
    >
      <igntr-icon name="search" [size]="16" class="text-slate-400"></igntr-icon>
      <input
        type="text"
        name="searchQuery"
        [(ngModel)]="searchQuery"
        [placeholder]="'searchOrganisation' | transloco"
        class="flex-1 bg-transparent text-[14px] text-slate-700 placeholder:text-slate-400 focus:outline-none"
      />
      <span class="font-mono text-[11.5px] text-slate-400">
        {{ visibleCount }} af {{ tenantsOptions.length }}
      </span>
    </div>

    <!-- Scrollable tenant list ------------------------------------------- -->
    <div class="igntr-tenant-list w-full flex-1 overflow-y-auto pb-4">
      <!-- Pinned section -->
      <ng-container *ngIf="visiblePinnedTenant as pinned">
        <div class="mt-1 flex w-full items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-400">
          <igntr-icon name="star" [size]="11" class="text-brand"></igntr-icon>
          {{ "lastVisited" | transloco }}
        </div>

        <!-- Pinned card. Clicking SELECTS only; nothing navigates until
             the user confirms with "Continue to IGNTR". The orange border
             follows `selectedTenantId`, so it can move to another card. -->
        <button
          type="button"
          (click)="selectedTenantId = pinned.value"
          class="mt-3 flex w-full items-center gap-4 rounded-2xl bg-white px-4 py-4 text-left transition"
          [class.shadow-\[0_4px_20px_rgba\(215\,122\,58\,0\.10\)\]]="selectedTenantId === pinned.value"
          [style.border]="selectedTenantId === pinned.value ? '2px solid var(--igntr-brand)' : '1px solid #e2e8f0'"
        >
          <div
            class="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-[14px] font-semibold text-white"
            [style.background]="
              'linear-gradient(135deg, oklch(0.55 0.18 250), oklch(0.42 0.16 250))'
            "
          >
            {{ initials(pinned.label) }}
            <!-- Small orange dot indicator on top-right of the avatar. -->
            <span class="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-brand ring-2 ring-white"></span>
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2">
              <span class="truncate text-[15px] font-semibold text-slate-900">
                {{ pinned.label }}
              </span>
              <span class="rounded-md bg-brand/10 px-1.5 py-0.5 font-mono text-[10.5px] text-brand">
                {{ "admin" | transloco }}
              </span>
            </div>
            <div class="mt-1 flex items-center gap-2 text-[12px] text-slate-500">
              <span class="inline-flex items-center gap-1">
                <igntr-icon name="users" [size]="12"></igntr-icon>
                {{ pinned.memberCount }}
              </span>
              <span class="inline-flex items-center gap-1 text-brand">
                <igntr-icon name="fire" [size]="12"></igntr-icon>
                {{ pinned.activeCount }} {{ "nActive" | transloco }}
              </span>
              <!-- "Last accessed" timestamp hidden for now: LastLoginTime is
                   global per user, so it can't be made per-tenant. -->
            </div>
          </div>
          <div
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition"
            [class.bg-brand]="selectedTenantId === pinned.value"
            [class.text-white]="selectedTenantId === pinned.value"
            [class.bg-slate-100]="selectedTenantId !== pinned.value"
            [class.text-slate-400]="selectedTenantId !== pinned.value"
          >
            <igntr-icon name="check" [size]="14"></igntr-icon>
          </div>
        </button>
      </ng-container>

      <!-- All other organisations -->
      <ng-container *ngIf="otherTenants.length">
        <div class="mt-8 flex w-full items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-400">
          {{ "allOrganisations" | transloco }}
        </div>

        <div class="mt-3 flex w-full flex-col gap-2.5">
          <!-- Clicking SELECTS. The currently-selected card gets the same
               orange-border treatment as the pinned card. -->
          <button
            *ngFor="let option of otherTenants; let i = index"
            type="button"
            (click)="selectedTenantId = option.value"
            class="group flex w-full items-center gap-4 rounded-2xl bg-white px-4 py-3.5 text-left transition hover:bg-slate-50"
            [style.border]="selectedTenantId === option.value ? '2px solid var(--igntr-brand)' : '1px solid #e2e8f0'"
          >
            <div
              class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-[13px] font-semibold text-white"
              [style.background]="avatarGradient(option.label)"
            >
              {{ initials(option.label) }}
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <span class="truncate text-[14.5px] font-semibold text-slate-900">
                  {{ option.label }}
                </span>
                <span class="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono text-[10.5px] text-slate-600">
                  {{ "member" | transloco }}
                </span>
              </div>
              <div class="mt-0.5 flex items-center gap-2 text-[12px] text-slate-500">
                <span class="inline-flex items-center gap-1">
                  <igntr-icon name="users" [size]="12"></igntr-icon>
                  {{ option.memberCount }}
                </span>
                <span class="inline-flex items-center gap-1 text-brand">
                  <igntr-icon name="fire" [size]="12"></igntr-icon>
                  {{ option.activeCount }} {{ "nActive" | transloco }}
                </span>
                <!-- "Last accessed" timestamp hidden for now: LastLoginTime is
                     global per user, so it can't be made per-tenant. -->
              </div>
            </div>
            <!-- Right side: filled check if selected, otherwise outline-circle arrow. -->
            <span
              *ngIf="selectedTenantId === option.value"
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand text-white"
            >
              <igntr-icon name="check" [size]="14"></igntr-icon>
            </span>
            <span
              *ngIf="selectedTenantId !== option.value"
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 transition group-hover:text-slate-600"
              style="border: 1px solid #e2e8f0;"
            >
              <igntr-icon name="arrowR" [size]="14"></igntr-icon>
            </span>
          </button>
        </div>
      </ng-container>

      <app-error-alert
        *ngIf="error"
        class="mt-6 w-full"
        title="To login by microsoft 365, please log out of other Microsoft services in this browser and try again"
        errors="error"
      ></app-error-alert>
    </div>
  </main>

  <!-- Bottom action bar — outside the scrollable list, always visible. -->
  <div class="shrink-0" style="border-top: 1px solid #e2e8f0;">
    <div class="mx-auto flex max-w-[720px] items-center gap-3 px-4 py-3 sm:px-6 sm:py-4">
      <button
        type="button"
        *ngIf="tenantsOptions.length > 0"
        (click)="selectedTenantId && setActiveTenant(selectedTenantId)"
        [disabled]="!selectedTenantId"
        class="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-brand py-3 text-[14px] font-semibold text-white shadow-sm transition hover:bg-brand-hot disabled:opacity-60"
      >
        {{ "continueToIgntr" | transloco }}
        <igntr-icon name="arrowR" [size]="14"></igntr-icon>
      </button>
      <button
        type="button"
        (click)="skip()"
        class="rounded-xl bg-white px-5 py-3 text-[14px] font-medium text-slate-700 transition hover:bg-slate-50"
        style="border: 1px solid #e2e8f0;"
      >
        {{ "skipForNow" | transloco }}
      </button>
    </div>
  </div>

  <!-- Footer -->
  <footer class="flex shrink-0 items-center justify-between px-8 pb-4 pt-2 text-[11.5px] text-slate-400 lg:px-16">
    <span>© 2026 IGNTR</span>
    <span class="font-mono">v4.2.7</span>
  </footer>
</div>
